import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, LogIn, User, ArrowLeft, Users, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [demoUsers, setDemoUsers] = useState([]);
  const [demoModeEnabled, setDemoModeEnabled] = useState(true);
  const [showDemoDropdown, setShowDemoDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    checkIfLoggedIn();
    checkDemoMode();
  }, []);

  const checkIfLoggedIn = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const user = await res.json();
        if (user?.user_id) {
          navigate(`/profile/${user.user_id}`, { replace: true });
        }
      }
    } catch {}
  };

  const checkDemoMode = async () => {
    try {
      const configRes = await fetch(`${API_URL}/api/config/demo-mode`);
      const configData = await configRes.json();
      setDemoModeEnabled(configData.demo_mode !== false);

      if (configData.demo_mode !== false) {
        const usersRes = await fetch(`${API_URL}/api/auth/demo-users`);
        const usersData = await usersRes.json();
        setDemoUsers(usersData);
      }
    } catch (error) {
      console.error('Failed to check demo mode');
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const clearError = (field) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    let hasErrors = false;
    const newErrors = {};
    
    if (!identifier) {
      newErrors.identifier = 'Email is required';
      hasErrors = true;
    } else if (identifier.includes('@') && !isValidEmail(identifier)) {
      newErrors.identifier = 'Not valid email';
      hasErrors = true;
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          toast.success('Login successful!');
          navigate(`/profile/${data.user.user_id}`, { replace: true });
        } else {
          setErrors({ general: 'Incorrect Username and Password Combination' });
        }
      } else {
        let errorMsg = 'Incorrect Username and Password Combination';
        try {
          const data = await res.json();
          if (data.detail && typeof data.detail === 'string') {
            errorMsg = data.detail;
          }
        } catch {}
        setErrors({ general: errorMsg });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Connection failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async (demoUser) => {
    setIsLoading(true);
    setShowDemoDropdown(false);

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          identifier: demoUser.email, 
          password: 'demo123' 
        }),
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok && data.user) {
        toast.success(`Logged in as ${demoUser.name}`);
        // Redirect all users to their profile page
        navigate(`/profile/${data.user.user_id}`, { replace: true });
      } else {
        setErrors({ general: 'Demo login failed' });
      }
    } catch (error) {
      setErrors({ general: 'Connection failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Note: Google OAuth will redirect to dashboard, then we can redirect to profile
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="glass-panel w-full max-w-md p-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold mb-2 tracking-tight">Welcome Back</h1>
          <p className="text-muted-foreground">Sign in to continue to Tsarona</p>
        </div>

        {/* Google Login - Primary CTA */}
        <Button
          data-testid="google-login-btn"
          type="button"
          className="w-full bg-white hover:bg-gray-100 text-gray-800 rounded-full py-6 font-bold text-base shadow-lg shadow-white/10 mb-6"
          onClick={handleGoogleLogin}
        >
          <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-card text-muted-foreground">Or sign in with email</span>
          </div>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-xl">
            <p className="text-red-500 text-sm">{errors.general}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="text-sm font-medium mb-2 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="login-identifier-input"
                type="text"
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); clearError('identifier'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.identifier ? 'border-red-500' : ''}`}
                placeholder="email@example.com"
              />
            </div>
            {errors.identifier && <p className="text-red-500 text-sm mt-1">{errors.identifier}</p>}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                className={`pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl ${errors.password ? 'border-red-500' : ''}`}
                placeholder="••••••••"
              />
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          <Button
            data-testid="login-submit-btn"
            type="submit"
            className="w-full bg-primary hover:scale-105 transition-transform rounded-full py-6 font-bold"
            disabled={isLoading}
          >
            <LogIn className="mr-2 h-5 w-5" />
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-card text-muted-foreground">Other options</span>
          </div>
        </div>

        <div className="space-y-3">
          {/* Demo User Button - Only shown if demo mode is enabled */}
          {demoModeEnabled && (
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className="w-full border-secondary text-secondary hover:bg-secondary/10 rounded-full py-6"
                onClick={() => setShowDemoDropdown(!showDemoDropdown)}
                disabled={isLoading}
              >
                <Users className="mr-2 h-5 w-5" />
                Demo User
                <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${showDemoDropdown ? 'rotate-180' : ''}`} />
              </Button>

              {/* Demo Users Dropdown */}
              {showDemoDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-white/10 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                  {demoUsers.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No demo users available
                    </div>
                  ) : (
                    demoUsers.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => handleDemoLogin(user)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
                      >
                        <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 text-secondary" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-sm">{user.stage_name || user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Don't have an account?{' '}
          <Link to="/join-talent-show" className="text-primary hover:underline font-medium">
            Sign up
          </Link>
        </p>
      </Card>
    </div>
  );
}

export default LoginPage;
